import type { QueryKey } from '@tanstack/react-query';

import { PERSIST_BUSTER, PERSIST_RESTORE_TIMEOUT_MS } from './queryConfig';

export type OfflineRestoreStatus =
  | 'idle'
  | 'restoring'
  | 'restored'
  | 'timeout'
  | 'error';

export interface PersistDecisionEvent {
  at: number;
  persisted: boolean;
  policy: string;
  queryHash: string | null;
  queryKey: string;
  reason: string;
}

export interface OfflineDebugState {
  currentUserId: string | null;
  isRestoring: boolean;
  lastPersistEvents: PersistDecisionEvent[];
  lastRestoreError: string | null;
  lastRestoreFinishedAt: number | null;
  lastRestoreStartedAt: number | null;
  lastRestoreStatus: OfflineRestoreStatus;
  lastRestoreUserId: string | null;
  persistBuster: string;
  restoreGeneration: number;
  restoreTimeoutMs: number;
}

const MAX_PERSIST_EVENTS = 12;

let state: OfflineDebugState = {
  currentUserId: null,
  isRestoring: true,
  lastPersistEvents: [],
  lastRestoreError: null,
  lastRestoreFinishedAt: null,
  lastRestoreStartedAt: null,
  lastRestoreStatus: 'idle',
  lastRestoreUserId: null,
  persistBuster: PERSIST_BUSTER,
  restoreGeneration: 0,
  restoreTimeoutMs: PERSIST_RESTORE_TIMEOUT_MS,
};

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function logDev(label: string, payload: unknown) {
  if (!import.meta.env.DEV) return;

  // eslint-disable-next-line no-console
  console.info(`[offline] ${label}`, payload);
}

export function subscribeOfflineDebug(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getOfflineDebugSnapshot(): OfflineDebugState {
  return state;
}

export function updateOfflineDebugState(
  patch: Partial<OfflineDebugState>,
  label?: string,
) {
  state = { ...state, ...patch };
  emit();

  if (label) {
    logDev(label, patch);
  }
}

export function formatQueryKey(queryKey: QueryKey): string {
  try {
    return JSON.stringify(queryKey);
  } catch {
    return '[unserializable-query-key]';
  }
}

export function recordPersistDecision(event: Omit<PersistDecisionEvent, 'at'>) {
  state = {
    ...state,
    lastPersistEvents: [{ ...event, at: Date.now() }, ...state.lastPersistEvents]
      .slice(0, MAX_PERSIST_EVENTS),
  };
  emit();
}

/** Reset de estado para testes. */
export function __resetOfflineDebugState() {
  state = {
    currentUserId: null,
    isRestoring: true,
    lastPersistEvents: [],
    lastRestoreError: null,
    lastRestoreFinishedAt: null,
    lastRestoreStartedAt: null,
    lastRestoreStatus: 'idle',
    lastRestoreUserId: null,
    persistBuster: PERSIST_BUSTER,
    restoreGeneration: 0,
    restoreTimeoutMs: PERSIST_RESTORE_TIMEOUT_MS,
  };
  listeners.clear();
}
